'use client'

import { useTranslations } from 'next-intl'
import { Stack } from '@christiankaindl/lyts'
import { type ReactNode, useState, useEffect, useRef } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shared/components/Tooltip/Tooltip'
import Text from '@shared/components/Text/Text'
import {
  useStoreState,
  useTooltipStore,
  type TooltipProps,
} from '@ariakit/react'
import LabelWithIcon from '../Legal/LabelWithIcon'
import { LoggedErrorBoundary } from '@shared/components/ErrorBoundary/LoggedErrorBoundary'
import Placeholder from '@shared/components/Placeholder/Placeholder'
import { retrievePreviewAction } from './retrievePreviewAction'
import { useRouter } from 'next/navigation'

interface HoverPreviewProps {
  children: ReactNode
  render?: TooltipProps['render']
  id?: string
  normId?: string
  enumeration?: string
}

type PreviewData = NonNullable<
  Awaited<ReturnType<typeof retrievePreviewAction>>
>

function usePreviewData(
  previewId: string | undefined,
  open: boolean,
  normId?: string,
  enumeration?: string,
) {
  const [data, setData] = useState<PreviewData | null>(null)
  const [error, setError] = useState(false)
  const fetchedRef = useRef<string | undefined>(undefined)
  const cacheKey = [previewId, normId, enumeration].join('|')

  useEffect(() => {
    if (!open || !previewId || fetchedRef.current === cacheKey) return

    let cancelled = false
    setData(null)
    setError(false)

    const opts = normId || enumeration ? { normId, enumeration } : undefined
    retrievePreviewAction(previewId, opts).then(
      (result) => {
        if (cancelled) return
        fetchedRef.current = cacheKey
        if (!result) return setError(true)
        setData(result)
      },
      (err) => {
        if (cancelled) return
        console.error(err)
        setError(true)
      },
    )

    return () => {
      cancelled = true
    }
  }, [previewId, open, cacheKey, normId, enumeration])

  return { data, error, loading: open && !!previewId && !data && !error }
}

export default function HoverPreview({
  children,
  render,
  id,
  normId,
  enumeration,
}: HoverPreviewProps) {
  const t = useTranslations('HoverPreview')
  const tooltip = useTooltipStore()
  const open = useStoreState(tooltip, 'open')
  const previewId = id ?? normId
  const { data, error, loading } = usePreviewData(
    previewId,
    open,
    normId,
    enumeration,
  )
  const router = useRouter()

  return (
    <LoggedErrorBoundary
      fallback={children}
      logLabel='[HoverPreview] Failed to render preview trigger'
      logContext={{ id, normId }}
    >
      <Tooltip timeout={100} placement='bottom-start' store={tooltip}>
        <TooltipTrigger
          render={render ?? <span />}
          onClick={(event) => {
            const anchor = event.target as Partial<HTMLAnchorElement>
            if (
              data?.snippet &&
              !event.metaKey &&
              !event.ctrlKey &&
              anchor.href
            ) {
              event.preventDefault()
              const snippet = data.snippet.slice(0, -1)
              const url = anchor.href + `#text=${encodeURIComponent(snippet)}`
              // Respect the trigger's own target (e.g. the PDF reference
              // overlay links open in a new tab) instead of forcing a
              // same-tab client navigation.
              if (anchor.target === '_blank') {
                window.open(url, '_blank', 'noopener,noreferrer')
              } else {
                router.push(url)
              }
            }
          }}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent
          gutter={3}
          style={{
            maxWidth: '24em',
            padding: 15,
            contain: 'layout',
            textAlign: 'left',
            borderRadius: 12,
            width: '100%',
          }}
        >
          {loading && (
            <Stack gap={0.5}>
              <Placeholder type='text' />
              <Placeholder type='text' />
            </Stack>
          )}
          {error && <Text>{t('errorOccurred')}</Text>}
          {data && <PreviewContent data={data} id={id} />}
        </TooltipContent>
      </Tooltip>
    </LoggedErrorBoundary>
  )
}

function PreviewContent({ data, id }: { data: PreviewData; id?: string }) {
  const isLaw = data.documentType === 'law' || data.documentType === 'clause'
  const text =
    data.abbreviation ??
    data.lawTitle ??
    data.caseNumber ??
    data.Rechtssatznummer ??
    data.Geschaeftszahl

  if (!text) return null

  return (
    <LoggedErrorBoundary
      fallbackRender={() => JSON.stringify({ id }, null, 2)}
      logLabel='[HoverPreview] Failed to render preview content'
      logContext={{ id, documentType: data.documentType }}
    >
      <Stack gap={0.5}>
        <LabelWithIcon
          size='small'
          text={text}
          iconType={isLaw ? 'law' : 'decision'}
        />
        {isLaw && (
          <Text maxLines={2} weight='bold' style={{ lineHeight: 1.3 }}>
            {data.enumeration && data.enumeration !== '§ 0' ? (
              <>{data.enumeration}&ensp;</>
            ) : (
              ''
            )}
            {data.title}
          </Text>
        )}
        {(data.content ?? data.snippet) && (
          <Text maxLines={4} color='secondary' style={{ lineHeight: 1.3 }}>
            <span style={{ color: 'black' }}>
              {data.para
                ? `Abs ${data.para as string}${data.z ? ` Z ${data.z as string}` : ''}: `
                : null}
            </span>
            {data.content ?? data.snippet}
          </Text>
        )}
      </Stack>
    </LoggedErrorBoundary>
  )
}
